/**
 * Senja API Utilities
 *
 * Based on the official Senja REST API documentation:
 * https://support.senja.io/articles/rest-api-wbnz4
 */

const SENJA_API_BASE = 'https://api.senja.io/v1';

/**
 * Fetch a single batch of testimonials
 */
const fetchSingleBatchTestimonials = async ({
  apiKey,
  limit,
  offset,
  filters,
}) => {
  const queryParams = new URLSearchParams({
    limit,
    offset: offset || 0,
    approved: 'true',
    sort: 'date',
    order: 'desc',
    ...filters,
  });

  const response = await fetch(
    `${SENJA_API_BASE}/testimonials?${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Senja API error: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();

  if (!data.testimonials || !Array.isArray(data.testimonials)) {
    throw new Error('Invalid response format from Senja API');
  }

  return normalizeSenjaTestimonials(data.testimonials);
};

/**
 * Fetch large number of testimonials from Senja REST API with pagination
 *
 * @param {Object} config - Configuration object
 * @param {string} config.apiKey - Your Senja API key
 * @param {number} config.totalLimit - Total number of testimonials to fetch (up to 1000)
 * @param {Object} config.filters - Optional filters
 * @returns {Promise<Array>} Array of normalized testimonial objects
 */
export const fetchLargeSenjaTestimonials = async (config = {}) => {
  const { apiKey, totalLimit = 1000, filters = {} } = config;

  if (!apiKey) {
    console.warn('Senja API key not provided, using mock testimonials');
    return generateMockTestimonials(Math.min(totalLimit, 50));
  }

  try {
    const allTestimonials = [];
    const batchSize = 100; // API max per request
    const maxBatches = Math.ceil(Math.min(totalLimit, 1000) / batchSize);

    console.log(
      `🔄 Fetching ${totalLimit} testimonials in ${maxBatches} batches...`,
    );

    for (let batch = 0; batch < maxBatches; batch++) {
      const offset = batch * batchSize;
      const limit = Math.min(batchSize, totalLimit - offset);

      if (limit <= 0) break;

      console.log(
        `📦 Fetching batch ${batch + 1}/${maxBatches} (${limit} testimonials)...`,
      );

      const batchTestimonials = await fetchSingleBatchTestimonials({
        apiKey,
        limit,
        offset,
        filters,
      });

      if (batchTestimonials.length === 0) {
        console.log(
          `⚠️ No more testimonials available after ${allTestimonials.length} testimonials`,
        );
        break;
      }

      allTestimonials.push(...batchTestimonials);

      // Stop if we received fewer testimonials than requested (no more available)
      if (batchTestimonials.length < limit) {
        console.log(
          `✅ Reached end of available testimonials: ${allTestimonials.length} total`,
        );
        break;
      }

      // Add small delay between requests to be respectful to the API
      if (batch < maxBatches - 1) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

    console.log(
      `✅ Successfully fetched ${allTestimonials.length} testimonials from Senja API`,
    );
    return allTestimonials;
  } catch (error) {
    console.error('Error fetching large batch from Senja API:', error);
    console.info('Falling back to mock testimonials');
    return generateMockTestimonials(Math.min(totalLimit, 50));
  }
};

/**
 * Filter testimonials by content length
 *
 * @param {Array} testimonials - Array of testimonial objects
 * @param {number} minLength - Minimum character count (default: 300)
 * @param {number} maxLength - Maximum character count (default: 500)
 * @returns {Array} Filtered testimonials
 */
export const filterTestimonialsByLength = (
  testimonials,
  minLength = 300,
  maxLength = 500,
) => {
  const filtered = testimonials.filter((testimonial) => {
    const contentLength = testimonial.content ? testimonial.content.length : 0;
    return contentLength >= minLength && contentLength <= maxLength;
  });

  console.log(
    `🔍 Filtered ${testimonials.length} testimonials to ${filtered.length} testimonials ` +
      `with ${minLength}-${maxLength} characters`,
  );

  return filtered;
};

/**
 * Fetch 1000 testimonials and filter by content length
 *
 * @param {string} apiKey - Senja API key
 * @param {Object} options - Configuration options
 * @param {number} options.minLength - Minimum content length (default: 300)
 * @param {number} options.maxLength - Maximum content length (default: 500)
 * @param {number} options.totalLimit - Total testimonials to fetch before filtering (default: 1000)
 * @returns {Promise<Array>} Filtered testimonials
 */
export const fetchAndFilterTestimonials = async (apiKey, options = {}) => {
  const {
    minLength = 300,
    maxLength = 500,
    totalLimit = 1000,
    ...otherFilters
  } = options;

  console.log(
    `🚀 Starting fetch of ${totalLimit} testimonials with ${minLength}-${maxLength} char filter...`,
  );

  try {
    // Fetch large batch of testimonials
    const allTestimonials = await fetchLargeSenjaTestimonials({
      apiKey,
      totalLimit,
      filters: otherFilters,
    });

    // Filter by content length
    const filteredTestimonials = filterTestimonialsByLength(
      allTestimonials,
      minLength,
      maxLength,
    );

    console.log(
      `✅ Final result: ${filteredTestimonials.length} testimonials ` +
        `(filtered from ${allTestimonials.length} total)`,
    );

    return filteredTestimonials;
  } catch (error) {
    console.error('Error in fetchAndFilterTestimonials:', error);

    // Fallback to mock data with appropriate length
    const mockData = generateMockTestimonials(50);
    return filterTestimonialsByLength(mockData, minLength, maxLength);
  }
};

/**
 * Fetch testimonials from Senja REST API (original function - keeping for compatibility)
 *
 * @param {Object} config - Configuration object
 * @param {string} config.apiKey - Your Senja API key (from project settings > integrations)
 * @param {Object} config.filters - Optional filters (rating, type, approved, etc.)
 * @param {number} config.limit - Limit number of testimonials (1-100, default 50)
 * @returns {Promise<Array>} Array of normalized testimonial objects
 */
export const fetchSenjaTestimonials = async (config = {}) => {
  const { apiKey, filters = {}, limit = 50 } = config;

  if (!apiKey) {
    console.warn('Senja API key not provided, using mock testimonials');
    return generateMockTestimonials(limit);
  }

  try {
    // Build query parameters
    const queryParams = new URLSearchParams({
      limit: Math.min(limit, 100), // API max is 100 per request
      approved: 'true', // Only get approved testimonials
      sort: 'date',
      order: 'desc',
      ...filters,
    });

    const response = await fetch(
      `${SENJA_API_BASE}/testimonials?${queryParams}`,
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Senja API error: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    if (!data.testimonials || !Array.isArray(data.testimonials)) {
      throw new Error('Invalid response format from Senja API');
    }

    console.log(
      `✅ Fetched ${data.testimonials.length} testimonials from Senja API`,
    );
    return normalizeSenjaTestimonials(data.testimonials);
  } catch (error) {
    console.error('Error fetching from Senja API:', error);
    console.info('Falling back to mock testimonials');
    return generateMockTestimonials(limit);
  }
};

/**
 * Normalize Senja API response to our component format
 *
 * @param {Array} senjaTestimonials - Raw testimonials from Senja API
 * @returns {Array} Normalized testimonial objects
 */
export const normalizeSenjaTestimonials = (senjaTestimonials) => {
  return senjaTestimonials.map((testimonial, index) => ({
    id: testimonial.id || index + 1,
    name: testimonial.customer_name || 'Anonymous',
    role:
      testimonial.customer_tagline ||
      testimonial.customer_company ||
      'Customer',
    avatar: generateAvatarUrl(
      testimonial.customer_name || 'Anonymous',
      testimonial.customer_avatar,
    ),
    content: cleanHtmlTags(testimonial.text || ''),
    title:
      testimonial.title || extractTitle(testimonial.text) || 'Great experience',
    rating: testimonial.rating || 5, // Default to 5 if rating is 0 or null
    platform: mapIntegrationToPlatform(testimonial.integration),
    date: testimonial.date || testimonial.created_at,
    url: testimonial.url,
    approved: testimonial.approved,
    type: testimonial.type, // 'text' or 'video'
    company: testimonial.customer_company,
    email: testimonial.customer_email,
    companyLogo: testimonial.customer_company_logo,
    videoUrl: testimonial.video_url,
    media: testimonial.media || [],
  }));
};

/**
 * Clean HTML tags from text content
 */
const cleanHtmlTags = (text) => {
  if (!text) return '';

  // Remove HTML tags like <mark>, <strong>, etc. but keep the content
  return text.replace(/<[^>]*>/g, '');
};

/**
 * Map Senja integration names to display-friendly platform names
 */
const mapIntegrationToPlatform = (integration) => {
  if (!integration) return 'Website';

  const platformMap = {
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    google: 'Google',
    facebook: 'Facebook',
    'product-hunt': 'Product Hunt',
    g2: 'G2',
    trustpilot: 'Trustpilot',
    capterra: 'Capterra',
    app_store: 'App Store',
    'play-store': 'Play Store',
    youtube: 'YouTube',
    yelp: 'Yelp',
    amazon: 'Amazon',
    airbnb: 'Airbnb',
  };

  return (
    platformMap[integration] ||
    integration.charAt(0).toUpperCase() + integration.slice(1)
  );
};

/**
 * Generate pastel colors for initial avatars
 */
const generatePastelColor = (name) => {
  if (!name) return '#E0E7FF'; // Default light blue

  // Use name to generate consistent color
  const colors = [
    '#FFE4E1', // Light pink
    '#E1F5FE', // Light cyan
    '#F3E5F5', // Light purple
    '#E8F5E8', // Light green
    '#FFF3E0', // Light orange
    '#E0E7FF', // Light blue
    '#FFECB3', // Light yellow
    '#FCE4EC', // Light rose
    '#E0F2F1', // Light teal
    '#F1F8E9', // Light lime
    '#FFF8E1', // Light amber
    '#F3E2A9', // Light khaki
    '#E8EAF6', // Light indigo
    '#FFCDD2', // Light red
    '#D1C4E9', // Light deep purple
  ];

  const hash = name
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

/**
 * Generate an initial avatar (first letter with pastel background) or return existing avatar URL
 */
export const generateAvatarUrl = (name, existingAvatar = null) => {
  // If there's an existing avatar from Senja, use it
  if (existingAvatar && existingAvatar.trim() !== '') {
    return existingAvatar;
  }

  // Create initial avatar data
  const initial = name && name.trim() ? name.trim()[0].toUpperCase() : '?';
  const backgroundColor = generatePastelColor(name);

  // Return a data object that the component can use to render an initial avatar
  return {
    type: 'initial',
    initial,
    backgroundColor,
    textColor: '#374151', // Dark gray text
  };
};

/**
 * Extract title from testimonial content
 */
export const extractTitle = (content) => {
  if (!content) return '';

  // Remove HTML tags first
  const cleanContent = cleanHtmlTags(content);

  // Get first sentence or first 60 characters
  const firstSentence = cleanContent.split(/[.!?]/)[0].trim();
  if (firstSentence.length <= 60 && firstSentence.length > 10) {
    return firstSentence;
  }

  return cleanContent.substring(0, 60).trim() + '...';
};

/**
 * Check if Senja API is accessible with given credentials
 */
export const checkSenjaAPIAccess = async (apiKey) => {
  if (!apiKey) return false;

  try {
    const response = await fetch(`${SENJA_API_BASE}/testimonials?limit=1`, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
    });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Get testimonials with automatic retry and fallback
 * Now supports fetching 1000 testimonials with content length filtering
 */
export const getSenjaTestimonialsWithFallback = async (
  apiKey,
  options = {},
) => {
  const {
    limit = 50,
    minLength = null,
    maxLength = null,
    totalLimit = 1000,
    enableLengthFiltering = false,
    ...otherOptions
  } = options;

  // If length filtering is enabled, use the new function
  if (enableLengthFiltering && (minLength || maxLength)) {
    console.log('🔍 Using length filtering for testimonials...');

    try {
      return await fetchAndFilterTestimonials(apiKey, {
        minLength: minLength || 300,
        maxLength: maxLength || 500,
        totalLimit,
        type: otherOptions.type,
        minRating: otherOptions.minRating,
        ...otherOptions.filters,
      });
    } catch (error) {
      console.error('Failed to fetch filtered testimonials:', error);
      const mockData = generateMockTestimonials(limit);
      return filterTestimonialsByLength(
        mockData,
        minLength || 300,
        maxLength || 500,
      );
    }
  }

  // Original functionality for backwards compatibility
  const config = {
    apiKey,
    limit,
    filters: {
      approved: 'true',
      sort: 'date',
      order: 'desc',
      type: otherOptions.type, // 'text', 'video', or undefined for both
      rating: otherOptions.minRating ? `${otherOptions.minRating}` : undefined,
      ...otherOptions.filters,
    },
  };

  try {
    return await fetchSenjaTestimonials(config);
  } catch (error) {
    console.error('Failed to fetch Senja testimonials:', error);
    return generateMockTestimonials(config.limit);
  }
};

/**
 * Mock testimonials for development and fallback
 */
export const generateMockTestimonials = (count = 20) => {
  const mockTestimonials = [
    {
      id: 1,
      name: 'Sarah Chen',
      role: 'Product Manager at TechCorp',
      avatar: generateAvatarUrl('Sarah Chen'),
      content:
        'This platform has completely transformed how we handle our workflows and project management across our entire organization. The AI assistance is incredibly intuitive and saves us hours every day, allowing our team to focus on strategic initiatives rather than repetitive tasks. Our team productivity has increased by 40% since we started using it, and the learning curve was minimal.',
      title: 'Transformed our workflows completely',
      rating: 5,
      platform: 'Website',
    },
    {
      id: 2,
      name: 'Marcus Rodriguez',
      role: 'Startup Founder',
      avatar: generateAvatarUrl('Marcus Rodriguez'),
      content:
        'As a startup founder, time is everything and resources are limited. This tool has been a game-changer for our small but mighty team of five people. The learning curve was practically non-existent, and we were seeing tangible results within the first week of implementation. What impressed me most was how quickly it scaled with our growing business needs.',
      title: 'Game-changer for our startup',
      rating: 5,
      platform: 'Product Hunt',
    },
    {
      id: 3,
      name: 'Dr. Emily Watson',
      role: 'Research Director',
      avatar: generateAvatarUrl('Dr. Emily Watson'),
      content:
        "The accuracy and speed of the AI models are impressive and have exceeded our expectations in every way. We've been able to process complex data sets that would have traditionally taken our research team weeks to analyze, and now we complete them in just a few hours. The ROI has been phenomenal, and the insights we're gaining are driving better research decisions.",
      title: 'Impressive accuracy and speed',
      rating: 5,
      platform: 'LinkedIn',
    },
    {
      id: 4,
      name: 'James Liu',
      role: 'Software Engineer',
      avatar: generateAvatarUrl('James Liu'),
      content:
        'Clean interface, powerful features, and excellent documentation make this platform stand out from the competition. Everything a developer could ask for is here, from robust APIs to comprehensive SDKs. The API is well-designed with clear endpoints, and the community support is top-notch with active forums and responsive maintainers who actually listen to feedback.',
      title: 'Everything a developer could ask for',
      rating: 5,
      platform: 'GitHub',
    },
    {
      id: 5,
      name: 'Anna Kowalski',
      role: 'Marketing Director',
      avatar: generateAvatarUrl('Anna Kowalski'),
      content:
        'Our marketing campaigns have never been more effective or data-driven than they are today. The insights we get from this platform help us target the right audience with surgical precision, leading to better engagement and higher conversion rates. Our ROI has improved significantly, with conversion rates up 60% compared to our previous campaigns!',
      title: 'Marketing campaigns more effective',
      rating: 5,
      platform: 'Twitter',
    },
    {
      id: 6,
      name: 'Robert Thompson',
      role: 'Operations Manager',
      avatar: generateAvatarUrl('Robert Thompson'),
      content:
        'This solution has streamlined our entire operations workflow in ways we never thought possible. What used to require multiple tools, platforms, and countless hours of manual coordination now happens seamlessly in one integrated place. The team loves how easy it is to collaborate, track progress, and maintain visibility across all our operational processes.',
      title: 'Streamlined our entire workflow',
      rating: 5,
      platform: 'Slack',
    },
    {
      id: 7,
      name: 'Lisa Park',
      role: 'UX Designer',
      avatar: generateAvatarUrl('Lisa Park'),
      content:
        "The user experience is thoughtfully designed with attention to every detail that matters to end users. Every interaction feels natural and purposeful, creating a seamless flow that enhances productivity rather than hindering it. It's clear that real UX principles and user research were applied throughout the entire product development cycle, resulting in an intuitive interface.",
      title: 'Thoughtfully designed user experience',
      rating: 5,
      platform: 'Dribbble',
    },
    {
      id: 8,
      name: 'Ahmed Hassan',
      role: 'Data Scientist',
      avatar: generateAvatarUrl('Ahmed Hassan'),
      content:
        "The machine learning capabilities are cutting-edge and represent the latest advances in AI technology. We've been able to build and deploy models that outperform our previous solutions by significant margins, with better accuracy and faster training times. The platform provides everything a data scientist needs, from data preprocessing to model deployment. It's truly a data scientist's dream tool.",
      title: 'Cutting-edge ML capabilities',
      rating: 5,
      platform: 'Kaggle',
    },
    // Add more testimonials with appropriate length...
    {
      id: 9,
      name: 'Sophie Martinez',
      role: 'Business Analyst',
      avatar: generateAvatarUrl('Sophie Martinez'),
      content:
        'The analytics dashboard provides insights we never had access to before, transforming how we make strategic business decisions. Decision-making has become significantly more data-driven across our entire organization, from executive leadership to front-line managers. The visualizations are clear, actionable, and tell compelling stories that drive real business impact.',
      title: 'Analytics dashboard provides new insights',
      rating: 5,
      platform: 'Website',
    },
    {
      id: 10,
      name: 'David Kim',
      role: 'CTO at InnovateLab',
      avatar: generateAvatarUrl('David Kim'),
      content:
        'Security, scalability, and performance - this platform delivers on all three critical fronts that matter most to enterprise customers. Our technical team was thoroughly impressed with the robust architecture, comprehensive security measures, and seamless integration capabilities. The platform handles our enterprise-level traffic without any performance degradation.',
      title: 'Delivers on security and performance',
      rating: 5,
      platform: 'TechCrunch',
    },
  ];

  // Repeat testimonials to reach desired count, ensuring they all have appropriate length
  const result = [];
  for (let i = 0; i < count; i++) {
    const testimonial = mockTestimonials[i % mockTestimonials.length];
    result.push({
      ...testimonial,
      id: i + 1,
    });
  }

  return result;
};

export default {
  fetchSenjaTestimonials,
  getSenjaTestimonialsWithFallback,
  normalizeSenjaTestimonials,
  checkSenjaAPIAccess,
  generateAvatarUrl,
  extractTitle,
  generateMockTestimonials,
  fetchLargeSenjaTestimonials,
  filterTestimonialsByLength,
  fetchAndFilterTestimonials,
};
